import React, { Component } from "react";

import {
  BarChart,
  Bar,
  ReferenceLine,
  ResponsiveContainer,
  YAxis,
  XAxis,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { Metric, CustomizedXAxisTick, Error } from "components/Utils/Utils.js";
import numeral from "numeraljs";
import Heading from "../Elements/Heading.js";

const BUDGET_PIE_COLORS = ["#16DE85", "#ECECEC"];

const expectedRangeStyle = {
  color: "#B1C2C9",
  fontSize: ".675rem",
  paddingLeft: "45%",
  paddingTop: "0",
};

const ReferenceLabel = (props) => {
  const { value, textAnchor, fontSize, viewBox, dy, dx } = props;

  const x = viewBox.width + viewBox.x + 5;
  const y = viewBox.height + viewBox.y;

  return (
    <text
      x={x}
      y={y}
      dy={dy}
      dx={dx}
      fill={value === "On Plan" ? "#11AB66" : "#FF283F"}
      fontSize={fontSize || 10}
      textAnchor={textAnchor}
    >
      {value}
    </text>
  );
};

class AdsCostSummary extends Component {
  renderGraphs = (chart, stats) => (
    <div className="row mb-4 pt-3">
      <div className="col-xl-6 col-md-12 mb-xl-0 mb-3">
        <div style={expectedRangeStyle}>Expected Range</div>
        <ResponsiveContainer height={80}>
          <BarChart data={chart} layout="vertical" height={50}>
            <defs>
              <linearGradient id="budget_bar" x1="0" y1="0" x2="1" y2="0">
                <stop offset="5%" stopColor="#FAA461" stopOpacity={1} />
                <stop offset="95%" stopColor="#FC6D52" stopOpacity={1} />
              </linearGradient>
            </defs>

            <Bar
              dataKey="spent"
              maxBarSize={40}
              stackId="budget"
              fillOpacity={1}
              fill="url(#budget_bar)"
            />
            <Bar
              dataKey="left"
              maxBarSize={40}
              stackId="budget"
              fillOpacity={1}
              fill="#eeeeee"
            />
            <ReferenceLine
              x={stats.budget_bar_chart[0].progress}
              stroke="#FC6D52"
              label={
                <ReferenceLabel value={stats.budget_bar_chart[0].on_plan} />
              }
              ifOverflow="extendDomain"
              strokeDasharray="3 3"
            />
            <ReferenceLine
              x={chart[0].ref_min}
              stroke="#B1C2C9"
              ifOverflow="extendDomain"
              strokeDasharray="3 3"
            />
            <ReferenceLine
              x={chart[0].ref_max}
              stroke="#B1C2C9"
              ifOverflow="extendDomain"
              strokeDasharray="3 3"
            />

            <YAxis
              type="category"
              axisLine={false}
              tickLine={false}
              mirror={false}
              dataKey="name"
            />
            <XAxis
              type="number"
              hide={true}
              interval={0}
              scale="auto"
              tick={<CustomizedXAxisTick />}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="col-xl-6 col-md-12">
        <div className="row">
          <div className="col-4">
            <Heading size={6} className={"mb-1"}>
              Spent as of Today
            </Heading>
            <Metric {...stats.bs_today} size={"sm"} type={"currency"} />
          </div>

          <div className="col-4">
            <Heading size={6} className={"mb-1"}>
              Amount Remaining
            </Heading>
            <Metric {...stats.bs_last7} size={"sm"} type={"currency"} />
          </div>

          <div className="col-4">
            <Heading size={6} className={"mb-1"}>
              % of Budget Remaining
            </Heading>

            <PieChart height={50} width={50}>
              <Pie
                dataKey="value"
                data={stats.bs_chart}
                outerRadius="100%"
                innerRadius="70%"
                startAngle={90}
                endAngle={-270}
              >
                {stats.bs_chart.map((entry, index) => (
                  <Cell key={`cell-${entry}`} fill={BUDGET_PIE_COLORS[index]} />
                ))}
              </Pie>
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                className="piechart-metric__xsmall"
              >
                {numeral(stats.bs_chart[0].value).format("0.0%")}
              </text>
            </PieChart>
          </div>
        </div>
      </div>
    </div>
  );

  render() {
    const { data } = this.props;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    return (
      <div className="col-12">
        <div className="row">
          <div className="col-12 col-md-6">
            <div className="row mt-xl-0 mt-4">
              <div className="col-8 mb-2">
                <Heading size={6} className={"mb-1"}>
                  Overall Client Cost Summary Last 7 Days
                </Heading>
                <Metric
                  {...data.cost_summary.cost_last7}
                  size={"lg"}
                  type={"currency"}
                />
              </div>
              <div className="col-4">
                <Heading size={6} className={"mb-1"}>
                  Previous 7 Days
                </Heading>
                <Metric
                  {...data.cost_summary.cost_previous7}
                  size={"md"}
                  type={"currency"}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-6">
                <Heading size={6} className={"mb-1"}>
                  Last 30 Days
                </Heading>
                <Metric
                  {...data.cost_summary.cost_last30}
                  size={"md"}
                  type={"currency"}
                />
              </div>
              <div className="col-6">
                <Heading size={6} className={"mb-1"}>
                  Year-To-Date
                </Heading>
                <Metric
                  {...data.cost_summary.cost_ytd}
                  size={"md"}
                  type={"currency"}
                />
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 mt-md-0 mt-3">
            {this.renderGraphs(data.bs_budget, data)}
          </div>
        </div>
      </div>
    );
  }
}

export default AdsCostSummary;
